import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { addRepair } from '@/lib/api/tickets.js';
import { parseApiError } from '@/lib/api/errors.js';
import { listRepairServices, type RepairService } from '@/lib/api/repair-services.js';
import { formatCurrency } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function servicePrice(service: RepairService) {
  return String(service.resolvedPrice ?? service.defaultPrice ?? '0');
}

export function AddRepairForm({
  deviceId,
  ticketId,
  makeId,
  modelId,
  initialMode = 'catalog',
  onSuccess,
}: {
  deviceId: string;
  ticketId: string;
  makeId?: string | null | undefined;
  modelId?: string | null | undefined;
  initialMode?: 'catalog' | 'custom';
  onSuccess: () => void;
}) {
  const [mode, setMode] = useState<'catalog' | 'custom'>(initialMode);
  const [search, setSearch] = useState('');
  const [selectedService, setSelectedService] = useState<RepairService | null>(null);
  const [serviceName, setServiceName] = useState('');
  const [price, setPrice] = useState('0');

  const servicesQuery = useQuery({
    queryKey: ['repair-services', makeId, modelId],
    queryFn: () =>
      listRepairServices({
        makeId: makeId ?? undefined,
        modelId: modelId ?? undefined,
        displayOnPos: true,
        requireModelPricing: Boolean(modelId),
      }),
    enabled: mode === 'catalog' && Boolean(modelId),
  });

  const filteredServices = useMemo(
    () =>
      (servicesQuery.data ?? []).filter((service) =>
        service.name.toLowerCase().includes(search.trim().toLowerCase()),
      ),
    [search, servicesQuery.data],
  );

  const mutation = useMutation({
    mutationFn: () =>
      addRepair(ticketId, {
        ticketDeviceId: deviceId,
        serviceId: selectedService?.id,
        serviceName,
        quotedPrice: Number(price),
        warrantyDays: selectedService?.resolvedWarrantyDays ?? selectedService?.defaultWarrantyDays ?? undefined,
        warrantyType:
          selectedService?.resolvedWarrantyType ??
          selectedService?.defaultWarrantyType ??
          undefined,
      }),
    onSuccess: () => {
      toastSuccess(`${serviceName} added - ${formatCurrency(price)}`);
      setSearch('');
      setSelectedService(null);
      setServiceName('');
      setPrice('0');
      onSuccess();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function selectService(service: RepairService) {
    setSelectedService(service);
    setSearch(service.name);
    setServiceName(service.name);
    setPrice(servicePrice(service));
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!serviceName.trim()) {
      toastError('Repair service name is required');
      return;
    }
    const priceNum = Number(price);
    // $0 is valid (warranty, goodwill, covered-under-protection, etc.)
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      toastError('Repair price cannot be negative');
      return;
    }
    mutation.mutate();
  }

  return (
    <form className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4" onSubmit={submit}>
      <div className="flex flex-wrap gap-2">
        {(['catalog', 'custom'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => {
              setMode(option);
              setSelectedService(null);
              setSearch('');
              setServiceName('');
              setPrice('0');
            }}
            className={[
              'rounded-lg px-3 py-1.5 text-xs font-semibold capitalize',
              mode === option ? 'bg-primary text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200',
            ].join(' ')}
          >
            {option === 'catalog' ? 'From Catalog' : 'Custom Repair'}
          </button>
        ))}
      </div>

      {mode === 'catalog' && !modelId ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Select a device with a known model before choosing a repair from the catalog.
        </p>
      ) : null}

      {mode === 'catalog' && modelId ? (
        <div className="relative">
          <input
            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-primary"
            placeholder="Search repair catalog..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setSelectedService(null);
            }}
          />
          {search && !selectedService ? (
            <div className="absolute z-30 mt-2 max-h-72 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
              {filteredServices.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 text-left last:border-b-0 hover:bg-slate-50"
                  onClick={() => selectService(service)}
                >
                  <span>
                    <span className="block font-semibold text-slate-950">{service.name}</span>
                    <span className="text-xs text-slate-500">
                      {service.hasPriceOverride ? 'Device price' : 'Default price'}
                    </span>
                  </span>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(service.resolvedPrice ?? service.defaultPrice ?? '0')}
                  </span>
                </button>
              ))}
              {filteredServices.length === 0 ? (
                <div className="px-4 py-3 text-sm text-slate-500">No services found.</div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-[1fr_140px_auto]">
        <input
          className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-primary"
          placeholder="Repair service name"
          value={serviceName}
          onChange={(event) => setServiceName(event.target.value)}
        />
        <input
          className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-primary"
          type="number"
          min="0"
          step="0.01"
          value={price}
          onChange={(event) => setPrice(event.target.value)}
        />
        <button
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Adding...' : 'Add Repair'}
        </button>
      </div>
    </form>
  );
}
